import { useEffect, useState } from 'react';

import { CancelButton, SaveButton } from '@lib/core/src';
import Modal from '@lib/core/src/components/Modal';
import palette from '@lib/core/src/theme/palette';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';

export const BlockModal: React.FC<{
  isEdit: boolean;
  title?: string;
  prompt?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (title: string, prompt: string) => void;
}> = ({ prompt, title, isEdit, open, onClose, onConfirm }) => {
  const [blockTitle, setTitle] = useState<string>();
  const [blockPrompt, setPrompt] = useState<string>();

  useEffect(() => {
    if (isEdit) {
      setTitle(title || '');
      setPrompt(prompt || '');
    }
  }, [title, prompt, isEdit]);

  const onModalClose = () => {
    setTitle('');
    setPrompt('');
    onClose();
  };

  return (
    <Modal open={open} sx={{ width: 505 }} onClose={onModalClose}>
      <Stack gap={'32px'}>
        <Stack flexDirection="row" justifyContent={'space-between'}>
          <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>
            {isEdit ? 'Edit Block' : 'Create Block'}
          </Typography>
          <IconButton onClick={onModalClose}>
            <CloseIcon sx={{ color: palette.black, fontSize: '20px' }} />
          </IconButton>
        </Stack>
        <Stack gap={'20px'}>
          <Box>
            <Typography sx={{ fontWeight: 500, fontSize: '11px', marginBottom: '6px' }}>
              Title
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={blockTitle}
              defaultValue={isEdit ? title : ''}
              onChange={e => setTitle(e.target.value)}
              slotProps={{
                htmlInput: {
                  sx: {
                    fontSize: 12
                  }
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '7px',
                  background: palette.white
                }
              }}
            />
          </Box>
          <Box>
            <Stack flexDirection={'row'} marginBottom={'6px'} justifyContent={'space-between'}>
              <Typography sx={{ fontWeight: 500, fontSize: '11px' }}>Prompt</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '11px', color: palette.grey[400] }}>
                {blockPrompt?.length}/250{' '}
              </Typography>
            </Stack>
            <TextField
              multiline
              fullWidth
              rows={4}
              defaultValue={isEdit ? prompt : ''}
              onChange={e => setPrompt(e.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: 250,
                  sx: {
                    fontSize: 12
                  }
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '7px',
                  padding: '8.5px 14px',
                  background: palette.white
                }
              }}
              value={blockPrompt}
              variant="outlined"
            />
          </Box>
        </Stack>
        <Stack flexDirection={'row'} justifyContent={'flex-end'} gap={'10px'}>
          <CancelButton onClick={onModalClose}>Cancel</CancelButton>
          <SaveButton
            disabled={!blockTitle || !blockPrompt}
            onClick={() => {
              onConfirm(blockTitle || '', blockPrompt || '');
              onModalClose();
            }}
          >
            Save
          </SaveButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
